'use client';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { IMAGES_BASE_URL } from '@/lib/config';
import { api } from '@/utils/api';
import { IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonInput, IonItem, IonLabel, IonList, IonPage, IonTextarea, IonTitle, IonToggle, IonToolbar } from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import TeeTable from '@/components/TeeTable';

const EditClubPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  // Define types for course data
  type TeeHole = {
    teeId: string;
    holeNumber: number;
    distance: number;
    par: number;
    handicap: number;
    parW?: number;
    handicapW?: number;
  };

  type Tee = {
    teeId: string;
    courseId: string;
    teeName: string;
    teeColor: string;
    gender: string;
    courseCr: number;
    courseSlope: number;
    courseCrW?: number;
    courseSlopeW?: number;
    totalDistance: number;
    holes: TeeHole[];
  };

  type CourseDetail = {
    courseId: string;
    facilityId: string;
    courseName: string;
    slug: string | null;
    holes: number;
    par: number;
    courseType: string;
    measure: number;
    guestPolicy: string;
    tees: Tee[];
    // Add properties needed by TeeTable component
    id?: number;
    name?: string;
    city?: string;
    country?: string;
    distance?: number;
    thumbnail?: string;
    courses?: any[];
  };

  // State for courses data
  const [coursesData, setCoursesData] = useState<CourseDetail[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);

  // Define type for form data
  type FormDataType = {
    id: string;
    name: string;
    address: string;
    city: string;
    country: string;
    phone: string;
    email: string;
    thumbnail: string;
    websiteUrl: string;
    description: string;
    zip: string;
    state: string;
    clubMembership: string;
    numberOfHoles: string;
    latitude: string;
    longitude: string;
    architect: string;
    drivingRange: boolean;
    puttingGreen: boolean;
    chippingGreen: boolean;
    practiceBunker: boolean;
    motorCart: boolean;
    pullCart: boolean;
    golfClubsRental: boolean;
    clubFitting: boolean;
    proShop: boolean;
    golfLessons: boolean;
    caddieHire: boolean;
    restaurant: boolean;
    receptionHall: boolean;
    changingRoom: boolean;
    lockers: boolean;
    lodgingOnSite: boolean;
    createdAt?: string;
    updatedAt?: string;
    [key: string]: string | boolean | undefined;
  };

  const [formData, setFormData] = useState<FormDataType>({
    id: '',
    name: '',
    address: '',
    city: '',
    country: '',
    phone: '',
    email: '',
    thumbnail: '',
    websiteUrl: '',
    description: '',
    zip: '',
    state: '',
    clubMembership: '',
    numberOfHoles: '',
    latitude: '',
    longitude: '',
    architect: '',
    drivingRange: false,
    puttingGreen: false,
    chippingGreen: false,
    practiceBunker: false,
    motorCart: false,
    pullCart: false,
    golfClubsRental: false,
    clubFitting: false,
    proShop: false,
    golfLessons: false,
    caddieHire: false,
    restaurant: false,
    receptionHall: false,
    changingRoom: false,
    lockers: false,
    lodgingOnSite: false,
    createdAt: '',
    updatedAt: '',
  });
  const { t } = useTranslation();
  useEffect(() => {
    if (courseId) {
      fetchCourseData();
      fetchCourseDetails();
    }
  }, [courseId]);

  // Fetch course details including holes and tees
  const fetchCourseDetails = async () => {
    if (!courseId) return;

    try {
      setLoadingCourses(true);
      const { data } = await api.get(`/golf-clubs/${courseId}/courses`);

      setCoursesData(data);
    } catch (error) {
      console.error('Error fetching course details:', error);
      toast.error('Failed to load course details');
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchCourseData = async () => {
    try {
      const response = await api.get(`/golf-clubs/${courseId}`);
      setFormData({
        ...response.data,
        thumbnail: response.data.thumbnail
          ? `${IMAGES_BASE_URL}/${courseId}.jpg`
          : `${IMAGES_BASE_URL}/${courseId}.jpg`,
      });

      document.title = `${formData.name} - Edit`;
    } catch (error) {
      console.error('Error fetching course:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCheckboxChange = (e: CustomEvent) => {
    if (e.target && 'name' in e.target) {
      const name = (e.target as any).name;
      const checked = e.detail.checked;
      setFormData({
        ...formData,
        [name]: checked,
      });
    }
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData({
          ...formData,
          thumbnail: reader.result as string,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      await api.post(`/golf-clubs/${courseId}`, formData);
      toast.success('Course saved successfully');

    } catch (error) {
      console.error('Error saving course:', error);
      toast.error('Failed to save course');
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton text={t('back')} defaultHref={`/clubs/${formData.slug}`} />
          </IonButtons>
          <IonTitle>{t('edit_club')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleSave} type="submit" expand="block">{t('save')}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {/* Tabs */}
        <div className="mb-8 max-w-4xl mx-auto p-2">
          <Tabs defaultValue="general">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="general">{t('general')}</TabsTrigger>
              <TabsTrigger value="courses">{t('courses')}</TabsTrigger>
              <TabsTrigger value="amenities">{t('amenities')}</TabsTrigger>
              <TabsTrigger value="advanced">{t('advanced')}</TabsTrigger>
            </TabsList>

            {/* General Info Tab */}
            <TabsContent value="general">
              <IonList lines="none" className="space-y-4">
                {/* Thumbnail Upload */}
                <div>
                  <div className="relative border size-48 border-input justify-center rounded-md items-center cursor-pointer">
                    {formData.thumbnail ? (
                      <img
                        src={`${IMAGES_BASE_URL}/${formData.id}.jpg`}
                        className="size-full object-cover rounded"
                      />
                    ) : (
                      <span className="text-gray-500">Click to upload</span>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={handleThumbnailChange}
                    />
                  </div>
                </div>

                {/* General Info Fields */}
                {[
                  { label: t('name'), name: 'name' },
                  { label: t('address'), name: 'address' },
                  { label: t('city'), name: 'city' },
                  { label: t('country'), name: 'country' },
                  { label: t('zip_code'), name: 'zip' },
                  { label: t('state'), name: 'state' },
                  { label: t('phone'), name: 'phone', type: 'tel' },
                  { label: t('website'), name: 'websiteUrl' },
                  { label: t('email'), name: 'email' },
                  { label: t('architect'), name: 'architect' },
                  { label: t('latitude'), name: 'latitude', type: 'number' },
                  { label: t('longitude'), name: 'longitude', type: 'number' },
                ].map(({ label, name, type }) => (
                  <IonItem key={name}>
                    <IonLabel position="stacked">{label}</IonLabel>
                    <IonInput
                      name={name}
                      value={formData[name]}
                      onIonInput={(e) => handleInputChange(e as any)}
                    />
                  </IonItem>
                ))}

                {/* Description */}
                <IonItem>
                  <IonLabel position="stacked">{t('description')}</IonLabel>
                  <IonTextarea
                    name="description"
                    value={formData.description}
                    onIonInput={(e) => handleInputChange(e as any)}
                    rows={4}
                  />
                </IonItem>
              </IonList>
            </TabsContent>

            {/* Amenities Tab */}
            <TabsContent value="amenities">
              <IonList>
                {[
                  { label: t('driving_range'), name: 'drivingRange' },
                  { label: t('putting_green'), name: 'puttingGreen' },
                  { label: t('chipping_green'), name: 'chippingGreen' },
                  { label: t('practice_bunker'), name: 'practiceBunker' },
                  { label: t('motor_cart'), name: 'motorCart' },
                  { label: t('pull_cart'), name: 'pullCart' },
                  { label: t('golf_rentals'), name: 'golfClubsRental' },
                  { label: t('club_fitting'), name: 'clubFitting' },
                  { label: t('pro_shop'), name: 'proShop' },
                  { label: t('golf_lessons'), name: 'golfLessons' },
                  { label: t('caddie_hire'), name: 'caddieHire' },
                  { label: t('restaurant'), name: 'restaurant' },
                  { label: t('reception_hall'), name: 'receptionHall' },
                  { label: t('changing_room'), name: 'changingRoom' },
                  { label: t('lockers'), name: 'lockers' },
                  { label: t('lodging_on_site'), name: 'lodgingOnSite' },
                ].map(({ label, name }) => (
                  <IonItem key={name}>
                    <IonLabel>{label}</IonLabel>
                    <IonToggle
                      slot="end"
                      name={name}
                      checked={formData[name]}
                      onIonChange={handleCheckboxChange}
                    />
                  </IonItem>
                ))}
              </IonList>
            </TabsContent>

            {/* Courses Tab */}
            <TabsContent value="courses">
              <div className="p-2">
                {loadingCourses ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : coursesData.length > 0 ? (
                  <div className="space-y-8">
                    {coursesData.map((course) => (
                      <div key={course.courseId}>
                        {course.tees && course.tees.length > 0 ? (
                          <div className="p-4">
                            {/* Course Scorecard Table */}
                            <TeeTable
                              course={{
                                ...course,
                                id: parseInt(course.courseId),
                                name: course.courseName,
                                city: '',
                                country: '',
                                distance: 0,
                                thumbnail: '',
                                courses: [],
                                latlon: null
                              }}
                              t={t}
                            />

                            {/* Tee information */}
                            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                              {course.tees.map((tee: Tee) => (
                                <div key={tee.teeId} className="border rounded-md p-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <div
                                      className="w-4 h-4 rounded-full border"
                                      style={{ backgroundColor: tee.teeColor.toLowerCase() }}
                                    ></div>
                                    <h5 className="font-medium">{tee.teeName}</h5>
                                  </div>

                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                      <span className="text-muted-foreground">{t('rating')}:</span> {tee.courseCr}
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Slope:</span> {tee.courseSlope}
                                    </div>
                                    {tee.courseCrW && (
                                      <div>
                                        <span className="text-muted-foreground">{t('rating_w')}:</span> {tee.courseCrW}
                                      </div>
                                    )}
                                    {tee.courseSlopeW && (
                                      <div>
                                        <span className="text-muted-foreground">{t('slope_w')}:</span> {tee.courseSlopeW}
                                      </div>
                                    )}
                                    <div>
                                      <span className="text-muted-foreground">{t('total_distance')}:</span> {tee.totalDistance}m
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="p-4">
                            <div className="text-center py-6 text-muted-foreground">
                              <p>{t('no_tee_information')} {course.courseName}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>{t('no_course_details')}</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Advanced Tab */}
            <TabsContent value="advanced">
              <IonList lines="none">
                <IonItem>
                  <IonLabel position="stacked">{t('id')}: {formData.id}</IonLabel>
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">{t('created_at')}: {formData.createdAt}</IonLabel>
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">{t('updated_at')}: {formData.updatedAt}</IonLabel>
                </IonItem>
              </IonList>
            </TabsContent>
          </Tabs>
        </div>
      </IonContent>
    </IonPage >
  );
};

export default EditClubPage;